import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { EvaluationTypeLabelsEnum, ProjectTypeLabelsEnum } from '@/app/utils/constants';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import { FolderIcon } from '@hugeicons/core-free-icons';
import { DocumentVersionViewerPage } from '@/app/components/shared/EntityDocuments/DocumentVersionPage';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(home)/files/$documentId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId, tenderPackageId, documentId } = Route.useParams();

  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  return (
    <DocumentVersionViewerPage
      documentId={documentId}
      renderBreadcrumbs={(documentName) => (
        <Breadcrumbs
          projectId={projectId}
          tenderPackageId={tenderPackageId}
          project={project}
          evaluation={evaluation}
          documentName={documentName}
        />
      )}
    />
  );
}

function Breadcrumbs({
  projectId,
  tenderPackageId,
  project,
  evaluation,
  documentName,
}: {
  projectId: string;
  tenderPackageId: string;
  project: { type: ProjectTypeLabelsEnum; name: string };
  evaluation: { name: string };
  documentName?: string;
}) {
  const t = useTranslations('pages.tenderHome');

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbMenu>
          <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
            <ProjectTypeIcon type={project.type} />
            {project.name}
          </BreadcrumbMenuItem>
        </BreadcrumbMenu>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem className="max-md:hidden">
          <BreadcrumbLink
            to="/project/$projectId/tender-packages/$tenderPackageId"
            params={{ projectId, tenderPackageId }}
          >
            <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
            <span className="line-clamp-1">{evaluation.name}</span>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem className="max-md:hidden">
          <BreadcrumbLink
            to="/project/$projectId/tender-packages/$tenderPackageId/files"
            params={{ projectId, tenderPackageId }}
          >
            <HugeiconsIcon icon={FolderIcon} />
            {t('nav.files')}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem>
          <BreadcrumbPage>{documentName ?? '...'}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
